import { hasAnyRole, ROLES, type Role } from '@/auth/roles';

import { accountEntries } from './navigation';
import { CONTENT_SECTIONS, STAFF_SECTION_ENTRIES, type SectionEntry } from './paths';

/**
 * What the top bar's search can find, and how it matches.
 *
 * Pages only, for now: the portal's content sections do not exist yet, so
 * there is nothing inside them to find. Each can add its own items to what
 * the search box offers when it is built.
 */

export type DestinationGroup = 'Sections' | 'Account' | 'Staff';

/** One page the search box can take somebody to. */
export interface Destination {
  /** "Drafts". */
  label: string;
  group: DestinationGroup;
  /** The section a sub-page or action sits in: "Notifications". */
  section?: string;
  path: string;
  /** The section's one-line description, matched and shown under a section. */
  hint?: string;
}

/**
 * Every page one person may reach, in navigation order.
 *
 * The same rules as the navigation and the account menu, so search never
 * offers what they would not: organization roles get the sections their role
 * allows; staff get their own area, or, while viewing an organization, its
 * sections and account entries as the menus give them. The route guard is
 * still what refuses a typed address.
 */
export function searchIndex(role: Role | null, viewing: boolean): Destination[] {
  const isStaff = role === ROLES.ACTIVIST_STAFF;
  const index: Destination[] = [];

  if (isStaff && !viewing) {
    for (const entry of STAFF_SECTION_ENTRIES) {
      index.push({ label: entry.label, group: 'Staff', path: entry.path });
    }
    return index;
  }

  // Viewing staff pass every organization section, as the route guard lets
  // them through; everybody else passes what their role allows.
  const allowed = (allow: readonly Role[]) => viewing || hasAnyRole(role, allow);

  for (const section of CONTENT_SECTIONS as readonly SectionEntry[]) {
    if (!allowed(section.allow)) continue;
    index.push({
      label: section.label,
      group: 'Sections',
      path: section.path,
      hint: section.hint,
    });
    // A sub-page or action takes its section's roles unless it states its own,
    // as the route table does.
    for (const entry of [
      ...(section.children ?? []),
      ...(section.action ? [section.action] : []),
    ]) {
      if (!allowed(entry.allow ?? section.allow)) continue;
      index.push({
        label: entry.label,
        group: 'Sections',
        section: section.label,
        path: entry.path,
      });
    }
  }

  for (const entry of accountEntries(role, viewing)) {
    index.push({ label: entry.label, group: 'Account', path: entry.path });
  }

  return index;
}

function words(text: string | undefined): string[] {
  return (text ?? '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/**
 * The destinations that match what was typed, best first.
 *
 * A word of the label starting with it comes first, then a word of the
 * section, then a word of the description or the label merely containing it.
 * Navigation order within each. Nothing for an empty query.
 */
export function matchDestinations(
  index: readonly Destination[],
  query: string,
  limit = 8,
): Destination[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const tiers: Destination[][] = [[], [], []];
  for (const destination of index) {
    if (words(destination.label).some((word) => word.startsWith(q))) {
      tiers[0].push(destination);
    } else if (words(destination.section).some((word) => word.startsWith(q))) {
      tiers[1].push(destination);
    } else if (
      words(destination.hint).some((word) => word.startsWith(q)) ||
      destination.label.toLowerCase().includes(q)
    ) {
      tiers[2].push(destination);
    }
  }
  return tiers.flat().slice(0, limit);
}
